import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { FormInput } from '../components/common/FormInput';
import { SelectInput } from '../components/common/SelectInput';
import { getTransactionById, updateTransaction } from '../services/transactions';
import { getCategories } from '../services/categories';

export const EditTransactionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        const data = await getTransactionById(id);
        // Handle different response structures
        const transaction = Array.isArray(data) ? data[0] : (data.transaction || data);
        setFormData({
          date: transaction.date,
          description: transaction.description,
          amount: transaction.amount,
          category: transaction.category,
          type: transaction.type,
        });
        setErrors({});
      } catch (error) {
        setErrors({ fetch: error.message || 'Failed to load transaction' });
      } finally {
        setIsFetching(false);
      }
    };
    fetchTransaction();
  }, [id]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        const categoryList = Array.isArray(data) ? data : (data.categories || data.results || []);
        setCategories(categoryList);
      } catch (error) {
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateTransaction(id, formData);
      navigate('/transactions');
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (isFetching) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading transaction...</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (errors.fetch) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-2xl">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {errors.fetch}
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="w-full max-w-2xl px-4 sm:px-6 lg:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Edit Transaction</h1>

        {formData && (
          <Card>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormInput
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
              />

              <FormInput
                label="Description"
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />

              <FormInput
                label="Amount"
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
              />

              <SelectInput
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={categoriesLoading}
                options={categories.map((cat) => ({
                  label: cat.name,
                  value: cat.id,
                }))}
              />

              <SelectInput
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                options={[
                  { label: 'Income', value: 'income' },
                  { label: 'Expense', value: 'expense' },
                ]}
              />

              {errors.submit && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errors.submit}
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Transaction'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/transactions')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </AuthenticatedLayout>
  );
};
